import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axiosInstance from '@api/axios';
import { urls } from '@api/urls';

import Button from '@mui/material/Button';
import { Box, Stack, Typography } from '@mui/material';

import { FiCheckCircle, FiLoader } from 'react-icons/fi';
import { PiWarningCircleBold } from 'react-icons/pi';
import { IconCancel, IconTrash } from '@tabler/icons-react';

import { Status } from '@api/notifications/model/enums';
import { Notification } from '@api/notifications/model/types';
import { notificationTimeLabel } from '../model/notification-time';
import { routes } from '@shared/constants/paths';
import { Toast } from '@components/Toast/ui/Toast';

import styles from './NotificationItem.module.scss';

// ZLAYT-1606: notifications from the older demand letter feature, retired
// with its pages and addresses. Their documents can no longer be opened, so
// they get no "View Document" button.
const RETIRED_DEMAND_TYPES = new Set([
  'demand',
  'demand_for_parsing',
  'document_parsing_preview',
  'parsing_document_process',
]);

interface NotificationItemProps {
  notification: Notification;
}

function NotificationItem(props: NotificationItemProps) {
  const { notification } = props;
  const navigate = useNavigate();

  const timeRun = notificationTimeLabel(notification);

  const handleButtonClick = () => {
    const {
      type,
      document_generator_id,
      parser_id,
      document_instance_id,
      use_case_version_id,
      use_case_id,
      deposition_summary_version_id,
    } = notification.additional_data;

    const showNotFoundToast = () => {
      toast(({ id }) => (
        <Toast
          id={id}
          type="error"
          title="Error"
          text="Document no longer exists"
        />
      ));
    };

    switch (type) {
      case 'document_generation':
        navigate(
          routes.superAdmin.multiDocumentAgent.demandLetter.edit(
            document_generator_id
          )
        );
        break;
      case 'document_prompt_preview':
        navigate(routes.superAdmin.documentPromptPreview.edit(parser_id));
        break;
      case 'document_instance':
        if (!document_instance_id) break;
        axiosInstance
          .get(urls.documentInstance.byId(Number(document_instance_id)))
          .then(() => {
            navigate(
              routes.myDocuments.demandLetter.view(document_instance_id)
            );
          })
          .catch((error) => {
            if (error?.response?.status === 404) {
              showNotFoundToast();
              return;
            }
            showNotFoundToast();
          });
        break;
      case 'use_case_version':
        // Prefer navigating by use_case_id (canonical route),
        // fallback to version id (resolve to use_case if possible)
        if (use_case_id) {
          axiosInstance
            .get(urls.useCase.byId(Number(use_case_id)))
            .then(() => {
              navigate(routes.myDocuments.useCase.view(Number(use_case_id)));
            })
            .catch(() => showNotFoundToast());
          break;
        }

        if (use_case_version_id) {
          const vId = Number(use_case_version_id);
          axiosInstance
            .get(urls.useCaseVersion.byId(vId))
            .then((res) => {
              const vs = res.data;
              const ucId =
                typeof vs.use_case === 'object' && vs.use_case
                  ? vs.use_case.id
                  : vs.use_case;
              if (ucId) {
                navigate(routes.myDocuments.useCase.view(Number(ucId)));
              } else {
                showNotFoundToast();
              }
            })
            .catch(() => showNotFoundToast());
        }
        break;
      case 'deposition_summary_version': {
        const vId = Number(deposition_summary_version_id);
        if (!vId) break;
        // Fetch version to get deposition_summary id, then go to view page
        axiosInstance
          .get(urls.depositionSummaryVersion.byId(vId))
          .then((res) => {
            const vs = res.data;
            const depId =
              typeof vs.deposition_summary === 'object' && vs.deposition_summary
                ? vs.deposition_summary.id
                : vs.deposition_summary;
            if (depId) {
              navigate(
                routes.myDocuments.depositionSummary.view(Number(depId))
              );
            }
          })
          .catch(() => {
            showNotFoundToast();
          });
        break;
      }
      default:
        break;
    }
  };

  const renderIcon = () => {
    // For deletions, always show a delete/trash icon (regardless of status)
    if ((notification as any)?.additional_data?.event === 'document_deleted') {
      return <IconTrash className={styles.deletedIcon} />;
    }
    switch (notification.notification_status) {
      case Status.Success:
        return <FiCheckCircle className={styles.successIcon} />;
      case Status.Pending:
        return <FiLoader className={styles.pendingIcon} />;
      case Status.Error:
        return <PiWarningCircleBold className={styles.errorIcon} />;
      case Status.Cancelled:
        return <IconCancel className={styles.cancelledIcon} />;
      default:
        return null;
    }
  };

  return (
    <Box
      display="flex"
      gap={2}
      alignItems="center"
      className={styles.notificationItem}
    >
      {/* Left Icon */}
      <Box
        flexShrink={0}
        width={40}
        height={40}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {renderIcon()}
      </Box>

      {/* Middle Content */}
      <Stack spacing={2} flexGrow={1} maxWidth="calc(100% - 160px)">
        <Typography variant="h6" className={styles.notificationHeader} noWrap>
          {notification.title}
        </Typography>
        <Typography
          variant="body2"
          className={styles.notificationDescription}
          noWrap
        >
          {notification.description}
        </Typography>
        {notification.additional_data.type &&
          !RETIRED_DEMAND_TYPES.has(notification.additional_data.type) &&
          notification.notification_status === Status.Success && (
            <Button variant="outlined" onClick={handleButtonClick} size="small">
              View Document
            </Button>
          )}
      </Stack>

      {/* Right Date */}
      <Box
        flexShrink={0}
        width={60}
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-start"
        className={styles.notificationTime}
      >
        <Typography variant="caption" className={styles.timeText}>
          {timeRun}
        </Typography>
      </Box>
    </Box>
  );
}

export default NotificationItem;
